import React, { useCallback } from 'react'
import { useHistory, generatePath } from 'react-router-dom'
import { Modal } from 'antd'
import { WarningOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { useAction } from '@agro-club/frontend-shared'

import { DigitalAsset } from '../../../../modules/domain/digitalAsset/types'
import { AssetType, Status } from '../../../../types/entities'
import DigitalAssetActions from '../../../../modules/domain/digitalAsset/duck'
import Routes from '../routes'

type UseDigitalAssetActionsProps = {
  digitalAsset: DigitalAsset
  canEditOrDelete: boolean
}

export const useDigitalAssetActions = ({ digitalAsset, canEditOrDelete }: UseDigitalAssetActionsProps) => {
  const { t } = useTranslation('digitalAsset')
  const history = useHistory()
  const [modal, contextHolder] = Modal.useModal()

  // Redux actions
  const softDeleteAction = useAction(DigitalAssetActions.softDeleteRequested)
  const restoreAction = useAction(DigitalAssetActions.restoreRequested)
  const permanentDeleteAction = useAction(DigitalAssetActions.permanentDeleteRequested)
  const downloadDigitalAssetAction = useAction(DigitalAssetActions.downloadFileRequested)
  const listAction = useAction(DigitalAssetActions.listRequested)

  // Action handlers
  const handleEdit = useCallback(() => {
    if (digitalAsset && canEditOrDelete) {
      history.push(generatePath(Routes.Edit, { id: digitalAsset.id }))
    }
  }, [digitalAsset, canEditOrDelete, history])

  const handleDownload = useCallback(() => {
    if (digitalAsset && digitalAsset.asset_type === AssetType.Document) {
      downloadDigitalAssetAction([digitalAsset.id])
    }
  }, [digitalAsset, downloadDigitalAssetAction])

  const handleDelete = useCallback(() => {
    if (digitalAsset && canEditOrDelete) {
      const confirmContent = React.createElement('p', null, digitalAsset.document_name || digitalAsset.link_url)
      modal.confirm({
        title: t('messages.deleteMessage'),
        content: confirmContent,
        icon: React.createElement(WarningOutlined),
        cancelText: t('button.noConfirm'),
        okText: t('button.yesConfirm'),
        onOk: () => {
          softDeleteAction([digitalAsset.id])
          listAction({ page: 1 })
        },
      })
    }
  }, [digitalAsset, canEditOrDelete, modal, t, softDeleteAction, listAction])

  const handleRestore = useCallback(() => {
    if (digitalAsset) {
      const confirmContent = React.createElement('p', null, digitalAsset.document_name || digitalAsset.link_url)
      modal.confirm({
        title: t('messages.restoreMessage'),
        content: confirmContent,
        icon: React.createElement(WarningOutlined),
        cancelText: t('button.noConfirm'),
        okText: t('button.yesConfirm'),
        onOk: () => {
          restoreAction([digitalAsset.id])
          listAction({ page: 1 })
        },
      })
    }
  }, [digitalAsset, modal, t, restoreAction, listAction])

  const handlePermanentDelete = useCallback(() => {
    if (digitalAsset) {
      const confirmContent = React.createElement('p', null, digitalAsset.document_name || digitalAsset.link_url)
      modal.confirm({
        title: t('messages.permanentDeleteMessage'),
        content: confirmContent,
        icon: React.createElement(WarningOutlined),
        onOk: () => {
          permanentDeleteAction([digitalAsset.id])
          listAction({ page: 1 })
        },
      })
    }
  }, [digitalAsset, modal, t, permanentDeleteAction, listAction])

  // Helper function to check if actions are available
  const canDownload = digitalAsset?.asset_type === AssetType.Document
  const isDeleted = digitalAsset?.status === Status.Deleted

  return {
    // Action handlers
    handleEdit,
    handleDownload,
    handleDelete,
    handleRestore,
    handlePermanentDelete,

    // State helpers
    canDownload,
    isDeleted,
    canEditOrDelete,

    // Modal context holder (needed for modals to work)
    contextHolder,
  }
}
